import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { Users, UserPlus, Printer, Upload, User } from 'lucide-react';

interface Resident {
  id: string;
  profile_id: string;
  room_number: string | null;
  join_code: string;
  status: string;
  created_at: string;
  full_name: string | null;
}

export function ResidentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRoom, setNewRoom] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<string>('');

  useEffect(() => { loadResidents(); }, [user]);

  const loadResidents = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('tenant_id').eq('id', user.id).single();
      if (!profile?.tenant_id) { setLoading(false); return; }
      setTenantId(profile.tenant_id);
      const { data: residentData } = await supabase.from('residents')
        .select('id, full_name, room_number, join_code, status, created_at')
        .eq('tenant_id', profile.tenant_id).eq('status', 'active')
        .order('created_at', { ascending: false });
      if (residentData) {
        setResidents(residentData.map((r: any) => ({
          id: r.id, profile_id: null, room_number: r.room_number,
          join_code: r.join_code, status: r.status, created_at: r.created_at,
          full_name: r.full_name || 'Unnamed Resident'
        })));
      }
    } catch (err) { console.error('Error loading residents:', err); }
    finally { setLoading(false); }
  };

  const addResident = async () => {
    if (!newName.trim()) { setError('Please enter a resident name'); return; }
    if (!tenantId) return;
    setSaving(true); setError('');
    try {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const { error: resErr } = await supabase.from('residents').insert({
        tenant_id: tenantId, full_name: newName.trim(),
        room_number: newRoom.trim() || null, join_code: code, status: 'active'
      });
      if (resErr) throw resErr;
      setNewName(''); setNewRoom(''); setShowAddForm(false);
      await loadResidents();
    } catch (err: any) { setError(err.message || 'Something went wrong'); }
    finally { setSaving(false); }
  };

  const deactivateResident = async (residentId: string) => {
    if (!confirm('Remove this resident? This cannot be undone.')) return;
    await supabase.from('residents').update({ status: 'inactive' }).eq('id', residentId);
    await loadResidents();
  };

  const importFromCsv = async () => {
    if (!csvText.trim() || !tenantId) return;
    setCsvImporting(true); setCsvResult('');
    const lines = csvText.trim().split('\n').filter(l => l.trim());
    const rows = lines.map(l => l.split(',').map(c => c.trim().replace(/"/g, '')));
    const dataRows = rows[0]?.[0]?.toLowerCase().includes('name') ? rows.slice(1) : rows;
    let success = 0; let failed = 0;
    for (const row of dataRows) {
      const fullName = row[0]; const roomNumber = row[1] || null;
      if (!fullName) { failed++; continue; }
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const { error: err } = await supabase.from('residents').insert({
        tenant_id: tenantId, full_name: fullName,
        room_number: roomNumber, join_code: code, status: 'active'
      });
      if (err) { failed++; } else { success++; }
    }
    setCsvResult(`${success} imported${failed > 0 ? `, ${failed} failed` : ''}`);
    setCsvText(''); await loadResidents(); setCsvImporting(false);
  };

  const exportJoinCodesPdf = () => {
    const pw = window.open('', '_blank');
    if (!pw) return;
    const cards = residents.map(r => `<div style="border:2px solid #7c6f5b;border-radius:12px;padding:20px;margin:10px;display:inline-block;width:280px;vertical-align:top;font-family:Arial,sans-serif;page-break-inside:avoid;"><div style="font-size:13px;color:#999;margin-bottom:4px;">DIPPS Family Join Code</div><div style="font-size:20px;font-weight:800;color:#1a1a1a;margin-bottom:8px;">${r.full_name || 'Resident'}</div>${r.room_number ? `<div style="font-size:13px;color:#999;margin-bottom:12px;">Room ${r.room_number}</div>` : ''}<div style="background:#f5f0ea;border-radius:8px;padding:12px;text-align:center;margin-bottom:12px;"><div style="font-size:28px;font-weight:900;color:#7c6f5b;letter-spacing:6px;font-family:monospace;">${r.join_code}</div></div><div style="font-size:11px;color:#666;text-align:center;">Share with ${r.full_name?.split(' ')[0] || 'the resident'}'s family at app.dipps.ca</div></div>`).join('');
    pw.document.write(`<!DOCTYPE html><html><head><title>DIPPS Join Codes</title><style>body{margin:20px;background:white;}@media print{.no-print{display:none;}}</style></head><body><div class="no-print" style="margin-bottom:20px;font-family:Arial;"><button onclick="window.print()" style="background:#7c6f5b;color:white;border:none;padding:10px 24px;border-radius:8px;font-size:16px;font-weight:700;cursor:pointer;">Print / Save PDF</button></div><div>${cards}</div></body></html>`);
    pw.document.close();
  };

  const copyJoinCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh] text-[rgb(var(--admin-text-muted))] text-base">Loading residents...</div>
  );

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-[rgb(var(--admin-text-primary))]">Residents</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">{residents.length} active resident{residents.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={exportJoinCodesPdf} disabled={residents.length === 0} className="px-4 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] transition-all duration-200 hover:opacity-80 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            <Printer className="w-4 h-4" /> Print Join Codes
          </button>
          <button onClick={() => setShowCsvImport(!showCsvImport)} className="px-4 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] transition-all duration-200 hover:opacity-80 active:scale-[0.98] flex items-center gap-2">
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <button onClick={() => setShowAddForm(!showAddForm)} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98] flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> + Add Resident
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <p className="text-sm text-[rgb(var(--admin-text-secondary))]">Total Residents</p>
          <p className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{residents.length}</p>
        </div>
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <p className="text-sm text-[rgb(var(--admin-text-secondary))]">Active Today</p>
          <p className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{residents.length}</p>
        </div>
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <p className="text-sm text-[rgb(var(--admin-text-secondary))]">Alerts</p>
          <p className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">0</p>
        </div>
      </div>

      {/* Add Resident Form */}
      {showAddForm && (
        <div className="rounded-3xl p-6 bg-[rgb(var(--admin-surface))] border-2 border-[rgb(var(--admin-accent))] shadow-warm">
          <h3 className="text-base font-semibold text-[rgb(var(--admin-text-primary))] mb-4">New Resident</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Full Name *</label>
              <input className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] outline-none" placeholder="e.g. Margaret Johnson" value={newName} onChange={e => setNewName(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Room Number</label>
              <input className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] outline-none" placeholder="e.g. 204" value={newRoom} onChange={e => setNewRoom(e.target.value)} />
            </div>
          </div>
          {error && (<div className="p-3 mb-4 rounded-xl bg-[rgb(var(--admin-danger)/0.1)] border border-[rgb(var(--admin-danger)/0.3)] text-[rgb(var(--admin-danger))] text-sm">{error}</div>)}
          <div className="flex gap-3">
            <button onClick={addResident} disabled={saving} className="px-5 py-2 rounded-2xl bg-[rgb(var(--admin-accent))] text-white font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-70">{saving ? 'Saving...' : 'Add Resident'}</button>
            <button onClick={() => { setShowAddForm(false); setError(''); setNewName(''); setNewRoom(''); }} className="px-4 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-muted))] font-medium transition-all duration-200 hover:opacity-80 active:scale-[0.98]">Cancel</button>
          </div>
        </div>
      )}

      {/* CSV Import */}
      {showCsvImport && (
        <div className="rounded-3xl p-6 bg-[rgb(var(--admin-surface))] border-2 border-[rgb(var(--admin-surface-2))] shadow-warm">
          <h3 className="text-base font-semibold text-[rgb(var(--admin-text-primary))] mb-2">Import Residents from CSV</h3>
          <p className="text-sm text-[rgb(var(--admin-text-muted))] mb-4">Format: Full Name, Room Number (one per line)</p>
          <textarea value={csvText} onChange={e => setCsvText(e.target.value)} placeholder="Paste CSV here..." rows={5} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm font-mono resize-y mb-4 outline-none" />
          {csvResult && (<div className="p-3 mb-4 rounded-xl bg-[rgb(var(--admin-success)/0.1)] border border-[rgb(var(--admin-success)/0.3)] text-[rgb(var(--admin-success))] text-sm font-medium">{csvResult}</div>)}
          <div className="flex gap-3">
            <button onClick={importFromCsv} disabled={csvImporting || !csvText.trim()} className="px-5 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] font-medium transition-all duration-200 hover:opacity-80 active:scale-[0.98] disabled:opacity-70 flex items-center gap-2"><Upload className="w-4 h-4" />{csvImporting ? 'Importing...' : 'Import'}</button>
            <button onClick={() => { setShowCsvImport(false); setCsvText(''); setCsvResult(''); }} className="px-4 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-muted))] font-medium transition-all duration-200 hover:opacity-80 active:scale-[0.98]">Cancel</button>
          </div>
        </div>
      )}

      {/* Resident List */}
      {residents.length === 0 ? (
        <div className="rounded-3xl p-12 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm text-center">
          <Users className="w-10 h-10 mx-auto mb-3 text-[rgb(var(--admin-text-muted))]" />
          <p className="text-[rgb(var(--admin-text-muted))]">No residents yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {residents.map(resident => (
            <div key={resident.id} className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm grid grid-cols-[1fr_auto_auto] items-center gap-4 transition-all duration-200 hover:bg-[rgb(var(--admin-surface-2))] active:scale-[0.98]">

              {/* Left: Avatar + Name + Room */}
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[rgb(var(--admin-surface-2))] flex items-center justify-center">
                  <User className="w-5 h-5 text-[rgb(var(--admin-text-muted))]" />
                </div>
                <div>
                  <div className="text-[rgb(var(--admin-text-primary))] font-medium">{resident.full_name}</div>
                  {resident.room_number && <div className="text-sm text-[rgb(var(--admin-text-muted))]">Room {resident.room_number}</div>}
                </div>
              </div>

              {/* Center: Join Code */}
              <div className="text-center">
                <p className="text-xs text-[rgb(var(--admin-text-muted))]">Join Code</p>
                <button onClick={() => copyJoinCode(resident.join_code)} className="text-sm font-mono text-[rgb(var(--admin-text-secondary))] hover:text-[rgb(var(--admin-accent))] transition-colors duration-200">
                  {copiedCode === resident.join_code ? 'Copied!' : resident.join_code}
                </button>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(`/admin/residents/${resident.id}`)} className="rounded-xl px-4 py-2 bg-[rgb(var(--admin-accent))] text-white text-sm font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98]">View</button>
                <button onClick={() => deactivateResident(resident.id)} className="rounded-xl px-3 py-2 text-[rgb(var(--admin-danger))] text-sm hover:bg-[rgb(var(--admin-danger)/0.1)] transition-all duration-200 active:scale-[0.98]">Remove</button>
              </div>

            </div>
          ))}
        </div>
      )}

    </main>
  );
}